import {ArrayUtil} from "../../../js/util/ArrayUtil";
import {OptionExpiry} from "./OptionExpiry";
import {Optional} from "../../../js/util/Optional";
import {HistoricalOptionRecord} from "../../price/historical/HistoricalPriceModels";
import {Expiry} from "../../expiry/ExpiryService";
import {Expiries} from "../../expiry/Expiries";
import {IsoDate, IsoDateString} from "../../../pseudo-types/DateTypes";
import {OptionExpiryUtil} from "./OptionExpiryUtil";
import {isNotNull} from "../../../js/util/UtilFunctions";
import moment = require("moment");

type HistoricalRecords = Array<HistoricalOptionRecord>;
type HistoricalRecordsMap = Map<IsoDate, HistoricalRecords>;

export class OptionChain {

    private expiryYears: Array<string>;
    private expiriesByYear: Map<string, Array<Expiry>>;
    private sortedExpiries: Array<OptionExpiry>;
    private expiriesByDate: Map<IsoDate, OptionExpiry>;
    private expiriesByOption: Map<string, OptionExpiry>;

    public constructor(public readonly underlying: string,
                       private options: HistoricalRecords,
                       private _expiries: Expiries) {
        this.init(this.options, this._expiries);
    }

    private init(options: HistoricalRecords, allExpiries: Expiries) {
        const grouped = this.groupByExpiry(options);
        this.expiriesByDate = this.mapToExpiry(grouped, allExpiries);

        this.sortedExpiries = Array.from(this.expiriesByDate.values())
                                   .sort(OptionExpiryUtil.naturalOrdering());

        this.expiriesByOption = this.mapToOptions(this.sortedExpiries);

        const optionExpiries = new Set(grouped.keys());
        const expiries = allExpiries.expiries
                                    .filter(e => optionExpiries.has(IsoDateString(e.date)));

        this.expiriesByYear = this.buildExpiryMap(expiries);
        this.expiryYears = Array.from(this.expiriesByYear.keys()).sort();
    }

    private groupByExpiry(options: HistoricalRecords): HistoricalRecordsMap {
        return ArrayUtil.mapBy(options, op => IsoDateString(op.expiry));
    }

    private mapToExpiry(groupedOptions: HistoricalRecordsMap, expiries: Expiries): Map<IsoDate, OptionExpiry> {
        const map = new Map<IsoDate, OptionExpiry>();
        Array.from(groupedOptions.entries())
             .forEach(([key, value]) => {
                 const expiry = expiries.expiryFor(key);
                 expiry.ifPresent(e => {
                     const optionExpiry = new OptionExpiry(key, e.expiryType, value);
                     map.set(key, optionExpiry);
                 });
             });
        return map;
    }

    private mapToOptions(expiries: Array<OptionExpiry>): Map<string, OptionExpiry> {
        const result = new Map<string, OptionExpiry>();
        expiries.forEach((exp) => {
            const records = exp.puts.concat(exp.calls);
            records.filter(isNotNull)
                   .map((rec) => rec.code)
                   .forEach((code) => {
                       result.set(code, exp);
                   });
        });
        return result;
    }

    private buildExpiryMap(expiries: Array<Expiry>): Map<string, Array<Expiry>> {
        const selectorFn = (exp: Expiry) => moment(exp.date).year().toString();
        return ArrayUtil.mapBy(expiries, selectorFn);
    }

    public expiriesForYear(year: string): Array<Expiry> {
        return Optional.of(year)
                       .map(y => this.expiriesByYear.get(y))
                       .orElseGet(() => []);
    }

    public years(): Array<string> {
        return this.expiryYears;
    }

    public expiry(date: IsoDate): Optional<OptionExpiry> {
        return Optional.of(date).map(d => this.expiriesByDate.get(d));
    }

    public expiryForCode(code: string): Optional<IsoDate> {
        return this.contractForCode(code)
                   .map(o => o.expiry)
                   .map(e => IsoDateString(e));
    }

    public nextExpiry(code: string): Optional<HistoricalOptionRecord> {
        const currentExpiry = this.expiriesByOption.get(code);
        if (currentExpiry == null) {
            return Optional.empty();
        }
        const maybeRecord = currentExpiry.get(code);
        if (maybeRecord.isEmpty()) {
            return Optional.empty();
        }
        const record = maybeRecord.get();
        const type = record.optionType;
        const index = this.sortedExpiries.findIndex(OptionExpiryUtil.equals(currentExpiry));

        return Optional.of(index)
                       .map((idx) => idx + 1)
                       .map((idx) => this.sortedExpiries[idx])
                       .flatMap((exp) => exp.closestToStrike(record.strike))
                       .map((pair) => pair.get(type));
    }

    public previousExpiry(code: string): Optional<HistoricalOptionRecord> {
        const currentExpiry = this.expiriesByOption.get(code);
        if (currentExpiry == null) {
            return Optional.empty();
        }
        const maybeRecord = currentExpiry.get(code);
        if (maybeRecord.isEmpty()) {
            return Optional.empty();
        }
        const record = maybeRecord.get();
        const type = record.optionType;
        const index = this.sortedExpiries.findIndex(OptionExpiryUtil.equals(currentExpiry));

        return Optional.of(index)
                       .map((idx) => idx - 1)
                       .map((idx) => this.sortedExpiries[idx])
                       .flatMap((exp) => exp.closestToStrike(record.strike))
                       .map((pair) => pair.get(type));
    }

    public nextStrike(code: string): Optional<HistoricalOptionRecord> {
        return Optional.of(code)
                       .map((c) => this.expiriesByOption.get(c))
                       .flatMap((exp) => exp.nextStrike(code));
    }

    public previousStrike(code: string): Optional<HistoricalOptionRecord> {
        return Optional.of(code)
                       .map((c) => this.expiriesByOption.get(c))
                       .flatMap((exp) => exp.previousStrike(code));
    }

    public contractForCode(code: string): Optional<HistoricalOptionRecord> {
        return Optional.of(code)
                       .map(c => this.options.find(o => o.code === c));
    }

    public get expiries(): Expiries {
        return this._expiries.clone();
    }
}
